import type { TicketPipelineDeleteValidation } from '@troia-v3/types';
import type { ConfirmOptions } from '@/shared/components/ui/ConfirmProvider';

export interface PipelineDeleteDecision {
  /** O que mostrar no `useConfirm()`. */
  options: ConfirmOptions;
  /**
   * `false` quando o diálogo é meramente informativo (o backend já disse que a
   * exclusão é impossível): o chamador NÃO deve disparar a mutation nem que o
   * usuário clique no botão de confirmar.
   */
  canProceed: boolean;
}

/**
 * Traduz o veredito de `GET /ticket-pipelines/validate-delete/:id` no diálogo
 * que o usuário vê.
 *
 * `validation === null` significa que a checagem falhou (rede, 500). Nesse caso
 * o diálogo é honesto sobre a incerteza e permite tentar — o backend rejeita
 * de novo em `DELETE /:id/with-validation` se for o caso, e o hook mostra o
 * toast de erro. É melhor do que travar o usuário por causa de uma consulta
 * auxiliar que caiu.
 *
 * A `message` do backend NUNCA é exibida: ela está em inglês
 * (`Cannot delete pipeline with N linked ticket(s)`, ticket-pipelines/repository.ts:117).
 * Só `linkedTicketsCount` é consumido.
 */
export function buildPipelineDeleteConfirm(
  pipelineName: string,
  validation: TicketPipelineDeleteValidation | null,
): PipelineDeleteDecision {
  if (validation === null) {
    return {
      canProceed: true,
      options: {
        title: 'Excluir pipeline',
        message: `Não foi possível verificar se o pipeline "${pipelineName}" tem chamados vinculados. Deseja tentar excluir mesmo assim?`,
        confirmButtonText: 'Tentar excluir',
        variant: 'danger',
      },
    };
  }

  if (!validation.canDelete) {
    const n = validation.linkedTicketsCount;
    const chamados = n === 1 ? '1 chamado vinculado' : `${n} chamados vinculados`;
    return {
      canProceed: false,
      options: {
        title: 'Não é possível excluir',
        message: `O pipeline "${pipelineName}" tem ${chamados}. Mova ou encerre esses chamados antes de excluir o pipeline.`,
        confirmButtonText: 'Entendi',
        cancelButtonText: 'Fechar',
        variant: 'default',
      },
    };
  }

  return {
    canProceed: true,
    options: {
      title: 'Excluir pipeline',
      message: `Deseja realmente excluir o pipeline "${pipelineName}"?`,
      warningMessage: 'Esta ação não pode ser desfeita.',
      confirmButtonText: 'Excluir',
      variant: 'danger',
    },
  };
}
